import React, { useEffect } from "react";
import {
    Grid,
    Button,
    Typography,
    Box,
    Paper,
    CircularProgress
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { useMenusController } from "./context/menus-controller";
import { MenuDrawer } from "./components/menu-drawer";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { MenuCard } from "./components/menu-card";

export const Menus = () => {
    const {
        dataListResponse,
        isLoadingData,
        loadData,
        openForm,
        deleteData,
        handleReorderMenus,
        loadCustomPages
    } = useMenusController();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        loadData();
        loadCustomPages();
    }, []);

    const handleDragEndMenus = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id && dataListResponse && Array.isArray(dataListResponse)) {
            const oldIndex = dataListResponse.findIndex(menu => menu.id === active.id);
            const newIndex = dataListResponse.findIndex(menu => menu.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                // Criar nova lista reordenada
                const newList = [...dataListResponse];
                const [removed] = newList.splice(oldIndex, 1);
                newList.splice(newIndex, 0, removed);

                // Enviar IDs na nova ordem
                const menuIds = newList.map(menu => menu.id);
                handleReorderMenus(menuIds);
            }
        }
    };

    return (
        <React.Fragment>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                        <Button
                            variant="contained"
                            sx={{ mt: 2 }}
                            startIcon={<AddIcon />}
                            onClick={() => openForm()}
                        >
                            Novo Menu
                        </Button>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    {isLoadingData ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : !dataListResponse || !Array.isArray(dataListResponse) || dataListResponse.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center', height: '400px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography variant="body1" color="textSecondary">
                                Nenhum menu cadastrado
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Clique em "Novo Menu" para criar o primeiro menu
                            </Typography>
                        </Paper>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEndMenus}
                        >
                            <SortableContext
                                items={dataListResponse.map(m => m.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {dataListResponse.map(menu => (
                                    <MenuCard
                                        key={menu.id}
                                        menu={menu}
                                        onEdit={openForm}
                                        onDelete={deleteData}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}
                </Grid>
            </Grid>

            <MenuDrawer />
        </React.Fragment>
    );
};
